import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import { MatDialog } from '@angular/material/dialog'
import { BlockFieldsService } from 'src/app/block-services/block-fields.service'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { BlockDataService } from '../../block-services/block-data.service'

@Component({
  selector: 'block-reviews',
  templateUrl: './block-reviews.component.html',
  styleUrls: ['./block-reviews.component.scss']
})
export class BlockReviewsComponent implements OnInit {
  @Input() importedBlockData: any
  @Input() nestedBlock: any = {}
  @Input() builderType: 'mobile' | 'website' = 'mobile'
  @ViewChild('reviewImageLightbox') reviewImageLightbox!: TemplateRef<any>

  blockData: any
  blockId = ''
  previewBlock = false
  previewFields = false
  styleFields = false
  readonly stars = [1, 2, 3, 4, 5]
  // Reviews with an image upload in flight, keyed by review id, so the loader shows on that review
  private uploadingReviews = new Set<any>()

  constructor(
    private blockDataService: BlockDataService,
    private blockFieldsService: BlockFieldsService,
    public builderService: BuilderService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    if (this.importedBlockData) {
      this.previewBlock = true
      this.previewFields = false
      this.styleFields = false
    }
    if (this.nestedBlock?.type === 'reviews') {
      this.blockData = this.nestedBlock
      this.blockId = this.nestedBlock.id
      this.previewBlock = true
    } else {
      this.blockData = this.importedBlockData ?? this.blockFieldsService.getReviewsBlockFields()
      if (this.importedBlockData?.id) {
        this.blockId = this.importedBlockData.id
      } else if (!this.blockId) {
        this.blockId = this.blockDataService.getUniqueId(2)
      }
    }
  }

  addBlockToPreview(initial: any = '') {
    const blockData = {
      component: BlockReviewsComponent,
      index: 'latest',
      element: this,
      initial
    }
    this.blockDataService.sendBlockData(blockData)
    this.builderService.blocksCollapsed = true
  }

  appendBlock($event: any) {
    const clonedBlockData = { ...this.blockData }
    this.blockId = this.blockDataService.appendBlocksData(clonedBlockData)
    this.addBlockToPreview()
  }

  showData(blockId: any) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) {
      blockId = this.blockDataService.getLatestBlockId()
    }
    this.blockFieldsService.clickedBlockId = blockId
    this.blockDataService.sendCurrentBlock(BlockReviewsComponent)
  }

  deleteBlock(blockId: any) {
    const images = (this.getBlockFields(blockId)?.data?.reviews || []).map((r: any) => r.image)
    this.blockDataService.removeBlockData(blockId)
    this.deleteUnusedImages(images)
  }

  getBlockFields(id: string) {
    if (this.importedBlockData) return this.importedBlockData
    return this.blockDataService.getSelectedBlockData(id)
  }

  trackReview(_index: number, review: any): any {
    return review.id ?? _index
  }

  checkSidebar() {
    return !this.previewBlock && !this.previewFields && !this.styleFields
  }

  checkPreview() {
    return this.previewBlock && !this.previewFields && !this.styleFields
  }

  checkData() {
    return this.previewFields
  }

  checkStyle() {
    return this.styleFields && !this.previewFields && !this.previewBlock
  }

  checkSettings() {
    return !this.checkSidebar() && !this.checkPreview()
  }

  showDataTab() {
    this.previewBlock = false
    this.previewFields = true
    this.styleFields = false
  }

  showStyleTab() {
    this.previewBlock = false
    this.previewFields = false
    this.styleFields = true
  }

  addReview(blockId: string) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) return
    const fields = this.getBlockFields(blockId)
    const cloned = { ...fields.data }
    cloned.reviews = [...(cloned.reviews || [])]
    cloned.reviews.push({
      id: Date.now(),
      author: 'New Customer',
      rating: 5,
      content: 'Great product! Highly recommend.',
      verified: true,
      date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      index: cloned.reviews.length
    })
    fields.data = cloned
  }

  dropReview(event: CdkDragDrop<any[]>) {
    if (!this.blockDataService.checkBlockAvailability(this.blockId)) return
    const fields = this.getBlockFields(this.blockId)
    const reviews = [...(fields.data.reviews || [])]
    moveItemInArray(reviews, event.previousIndex, event.currentIndex)
    reviews.forEach((r: any, i: number) => (r.index = i))
    fields.data = { ...fields.data, reviews }
  }

  deleteReview(blockId: string, reviewIndex: number) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) return
    const fields = this.getBlockFields(blockId)
    const cloned = { ...fields.data }
    cloned.reviews = [...(cloned.reviews || [])]
    const [removed] = cloned.reviews.splice(reviewIndex, 1)
    cloned.reviews.forEach((r: any, i: number) => (r.index = i))
    fields.data = cloned
    this.deleteUnusedImages([removed?.image])
  }

  /**
   * Upload an image for a review and store its URL on the review
   */
  processReviewImage(fileInput: any, blockId: string, review: any) {
    const file: File = fileInput.target.files[0]
    if (!file) return
    const key = this.reviewKey(review)
    this.uploadingReviews.add(key)
    const upload$ = this.builderService.standalone
      ? this.builderService.uploadImage(file, this.builderService.selectedProject, 'reviews')
      : this.builderService.uploadFileCustomerServer(file, this.builderService.selectedProject, '')
    upload$.subscribe(
      (res: any) => {
        this.uploadingReviews.delete(key)
        this.setReviewImage(blockId, review, res)
      },
      () => {
        this.uploadingReviews.delete(key)
        alert('error uploading file, please contact support')
      }
    )
    // Allow re-selecting the same file after removing it
    fileInput.target.value = ''
  }

  isUploading(review: any): boolean {
    return this.uploadingReviews.has(this.reviewKey(review))
  }

  private reviewKey(review: any) {
    return review.id ?? review
  }

  removeReviewImage(blockId: string, review: any) {
    const image = review.image
    if (image && this.setReviewImage(blockId, review, '')) this.deleteUnusedImages([image])
  }

  /**
   * Deletes review images from storage once nothing on the page shows them any more,
   * so a duplicated review or Reviews block keeps its picture
   */
  private deleteUnusedImages(images: string[]) {
    const pageData = JSON.stringify(this.blockDataService.blocksData ?? [])
    new Set(images.filter(Boolean)).forEach((image) => {
      if (!pageData.includes(image)) this.builderService.deleteImage(image).subscribe()
    })
  }

  /**
   * Looks the review up by id when the upload finishes, not by its position when it started,
   * so reordering or deleting reviews mid-upload can't attach the image to the wrong one
   */
  private setReviewImage(blockId: string, review: any, image: string): boolean {
    if (!this.blockDataService.checkBlockAvailability(blockId)) return false
    const fields = this.getBlockFields(blockId)
    const reviews: any[] = fields?.data?.reviews || []
    const reviewIndex = reviews.findIndex((r) => r === review || (review.id != null && r.id === review.id))
    if (reviewIndex === -1) return false
    /**
     * Modifying Nested Object Directly doesn't work, so we have to clone the data object first then modify it
     */
    const cloned = { ...fields.data }
    cloned.reviews = [...cloned.reviews]
    cloned.reviews[reviewIndex] = { ...cloned.reviews[reviewIndex], image }
    fields.data = cloned
    return true
  }

  openReviewImage(image: string) {
    this.dialog.open(this.reviewImageLightbox, {
      data: image,
      panelClass: 'review-lightbox-panel',
      backdropClass: 'review-lightbox-backdrop',
      maxWidth: '100vw',
      autoFocus: false
    })
  }

  reviewRatingChange(blockId: string, reviewIndex: number, rating: number) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) return
    const fields = this.getBlockFields(blockId)
    if (fields?.data?.reviews?.[reviewIndex] !== undefined) {
      fields.data.reviews[reviewIndex].rating = rating
    }
  }

  styleChange(style: string, blockId: string, event: any, type: string) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) return
    const fields = this.blockDataService.getSelectedBlockData(blockId)
    const cloned = { ...fields.style }
    cloned[style] = type === 'slider' ? event.target.value + 'px' : (event.value ?? event.target?.value)
    fields.style = cloned
  }
}
